/**
 * XKCD-style memorable password generator.
 *
 * Produces passwords like `coherences=postbag=wicked` — words separated
 * by random symbols, easy to remember, hard to brute-force.
 *
 * Algorithm matches xkpasswd defaults: 4 words, random separator from
 * a symbol set, optionally padded with digits.
 *
 * @see https://www.xkpasswd.net/
 */

/** Separators matching xkpasswd defaults */
const SEPARATORS = ['.', ',', '-', '+', '=', '~', '|', ':', '#', '!', '?', '*', '&', '^', '%', '@'];

/** Word list — top 1000 common English words (sufficient for memorable passwords) */
const WORDS = [
  'about','above','after','again','against','agree','allow','almost','alone','along',
  'already','also','always','among','animal','answer','appear','arrive','artist','assume',
  'attack','attend','August','author','basket','battle','become','before','behind','belong',
  'better','beyond','billion','board','bonus','bother','bottom','branch','breed','bridge',
  'bright','broken','budget','bundle','burden','bureau','button','cabin','cable','candy',
  'cargo','carry','catch','cause','cell','chair','chance','change','charge','choose',
  'circle','clever','client','closet','coach','coast','color','comet','coral','could',
  'count','court','cover','crash','crazy','cream','creek','cross','crowd','crown','cruel',
  'crush','curve','cycle','daily','dance','dealt','death','debut','delay','delta',
  'depth','devil','diary','dirty','dodge','donor','double','draft','drain','drama',
  'dream','drink','drive','drown','dying','eager','early','earth','eight','elite',
  'empty','enemy','enjoy','enter','equal','error','essay','event','every','exact',
  'exist','extra','faint','faith','fancy','fatal','fault','fever','fiber','field',
  'fifth','fifty','fight','final','first','flame','flash','fleet','flesh','float',
  'flood','floor','flour','fluid','focus','force','forge','forth','found','frame',
  'frank','fraud','fresh','front','fruit','fully','funny','gamma','genre','ghost',
  'giant','given','glass','globe','going','grace','grade','grain','grand','grant',
  'graph','grasp','grass','grave','great','green','greet','grief','grill','gross',
  'group','grove','grown','guard','guess','guest','guide','guild','habit','happy',
  'harsh','heart','heavy','hence','herbs','honey','horse','hotel','house','human',
  'ideal','image','imply','index','inner','input','irony','issue','ivory','jewel',
  'joint','judge','juice','knife','knock','label','large','laser','later','laugh',
  'layer','learn','least','leave','legal','lemon','level','light','limit','linen',
  'liver','local','login','loose','lover','lower','lucky','lunch','lying','magic',
  'major','maker','manor','march','match','maybe','mayor','media','mercy','metal',
  'micro','might','miner','minor','minus','model','money','month','moral','motor',
  'mount','mouse','mouth','movie','music','nerve','never','noble','noise','north',
  'noted','novel','nurse','ocean','offer','onset','opera','orbit','order','organ',
  'other','outer','owner','oxide','ozone','paint','panel','panic','paper','party',
  'paste','patch','pause','peace','peach','pearl','penny','phase','phone','photo',
  'piano','piece','pilot','pitch','pixel','pizza','place','plain','plane','plant',
  'plate','plaza','plead','pluck','plumb','plume','plump','point','polar','porch',
  'pound','power','press','price','pride','prime','print','prior','prize','probe',
  'proof','proud','prove','pulse','punch','pupil','purse','queen','quest','quick',
  'quiet','quite','quote','radar','radio','raise','range','rapid','ratio','reach',
  'react','ready','realm','rebel','refer','reign','relax','repay','reply','rider',
  'ridge','rifle','right','rigid','risky','rival','river','robin','robot','rocky',
  'rough','round','route','royal','rugby','ruler','rural','saint','salad','scale',
  'scene','scope','score','scout','scrap','sense','serve','setup','seven','shade',
  'shall','shame','shape','share','shark','sharp','shelf','shell','shift','shine',
  'shirt','shock','shoot','shore','short','shout','sight','sigma','silly','since',
  'sixty','skill','skull','slave','sleep','slide','slope','smart','smell','smile',
  'smoke','snake','solar','solid','solve','sorry','south','space','spare','speak',
  'speed','spend','spice','spill','spine','split','spoke','sport','spray','squad',
  'staff','stage','stain','stake','stale','stalk','stall','stamp','stand','stare',
  'start','state','steam','steel','steep','steer','stern','stick','stiff','still',
  'stock','stone','stood','store','storm','story','strip','stuck','study','stuff',
  'style','sugar','suite','sunny','super','surge','swear','sweet','swing','sword',
  'sworn','table','taste','teach','theme','thick','thing','think','third','those',
  'three','throw','thumb','tiger','tight','timer','tired','title','today','token',
  'total','touch','tough','towel','tower','toxic','trace','track','trade','trail',
  'train','trait','trash','treat','trend','trial','tribe','trick','truly','trump',
  'trunk','trust','truth','tumor','twist','ultra','uncle','under','union','unite',
  'until','upper','upset','urban','usage','usual','valid','valve','vault','venue',
  'verse','video','vigor','viral','virus','visit','vista','vital','vivid','vocal',
  'voice','wages','watch','water','weary','weird','wheel','where','which','while',
  'white','whole','whose','width','witch','woman','world','worry','worse','worst',
  'worth','would','wound','wrath','write','wrong','wrote','yacht','young','yours',
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomDigits(n: number): string {
  let s = '';
  for (let i = 0; i < n; i++) s += Math.floor(Math.random() * 10);
  return s;
}

/**
 * Generate a memorable password.
 *
 * @param complexity 1–4 words (default 4, matching xkpasswd level 3)
 * @returns Password string like `river=melon=crown=flame81`
 */
export function generatePassword(complexity = 3): string {
  const wordCount = Math.max(2, Math.min(complexity + 1, 6));
  const words = Array.from({ length: wordCount }, () => pick(WORDS));
  const sep = pick(SEPARATORS);
  const suffix = complexity >= 3 ? randomDigits(2) : '';
  return words.join(sep) + suffix;
}
